import {
    ArrowDown,
    ArrowUp,
    Box,
    Button,
    Checkmark,
    Disclosure,
    DisclosureContent,
    DisclosureTrigger,
    Success,
    TextBlock,
    useDisclosureContext,
    Warning,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "components/asset-management-system/asset-side-panel.scss";
import { resolveColor } from "utils/commonFunctions";

interface Props {
    testId: string;
    titleKey: string;
    descriptionKey: string;
    helperTextKey?: string;
    completed: boolean;
    actionLabelKey?: string;
    onAction?: () => void;
}

function RemediationStepTriggerContent(props: { completed: boolean; titleKey: string }) {
    const { t } = useTranslation();
    const { isOpen } = useDisclosureContext();

    return (
        <Box justifyContent="SpaceBetween" verticalAlignment="Center">
            <Box verticalAlignment="Center" gap="Xxs">
                {props.completed ? <Success /> : <Warning />}
                <TextBlock disableBottomSpacing>{t(props.titleKey)}</TextBlock>
            </Box>
            {isOpen ? <ArrowUp /> : <ArrowDown />}
        </Box>
    );
}

export function RemediationStepCard(props: Props) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = React.useState(!props.completed);

    React.useEffect(() => {
        setIsOpen(!props.completed);
    }, [props.completed]);

    return (
        <Box
            columnLayout
            className={classNames(style.remediationStepCard, {
                [style.remediationStepCardWarning]: !props.completed && !isOpen,
                [style.remediationStepCardCompleted]: props.completed && !isOpen,
            })}
        >
            <Disclosure isOpen={isOpen} onOpenChange={setIsOpen}>
                <DisclosureTrigger testId={props.testId}>
                    <RemediationStepTriggerContent completed={props.completed} titleKey={props.titleKey} />
                </DisclosureTrigger>
                <DisclosureContent>
                    <hr className={style.remediationStepDivider} />
                    <Box columnLayout gap="Xs">
                        <TextBlock disableBottomSpacing>{t(props.descriptionKey)}</TextBlock>
                        {props.helperTextKey && (
                            <TextBlock disableBottomSpacing className={style.remediationStepHelperText}>
                                {t(props.helperTextKey)}
                            </TextBlock>
                        )}
                        {props.completed && (
                            <Box
                                horizontalAlignment="Right"
                                verticalAlignment="Center"
                                className={style.remediationStepCompletedLabel}
                            >
                                <Checkmark color={resolveColor("--theme-success")} />
                                <TextBlock disableBottomSpacing>
                                    {t("AssetManagement.sidePanel.riskLevel.remediation.completed")}
                                </TextBlock>
                            </Box>
                        )}
                        {!props.completed && props.actionLabelKey && props.onAction && (
                            <Box horizontalAlignment="Right">
                                <Button variant="Primary" small onClick={props.onAction}>
                                    {t(props.actionLabelKey)}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DisclosureContent>
            </Disclosure>
        </Box>
    );
}
